import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe2, Coins, Wallet, Building2 } from 'lucide-react';
import type { EChartsOption } from 'echarts';
import { AppShell } from '@/components/app/AppShell';
import { EChart } from '@/components/charts/EChart';
import { usePayrollSummary } from '@/lib/payroll';
import { countryFlag } from '@/lib/countries';

const PALETTE = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#ef4444'];

const fmt = (cents: number | null | undefined, ccy: string) => {
  if (cents == null) return '—';
  const decimals = ['JPY', 'KRW'].includes(ccy) ? 0 : 2;
  return new Intl.NumberFormat(ccy === 'JPY' ? 'ja-JP' : 'en-US', { style: 'currency', currency: ccy, maximumFractionDigits: decimals }).format(cents / 100);
};

/**
 * Productized cross-border payroll view: one employer's total cost across
 * every jurisdiction, FX-normalized into the workspace reporting currency.
 * SmartHR is Japan-only and structurally can't show this.
 */
export function GlobalPayrollPage() {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const summary = usePayrollSummary(year); // month omitted → whole-year rollup

  const s = summary.data;
  const ccy = s?.reportingCurrency ?? '—';
  const cashOutlay = s ? s.totals.grossInReportingCents + s.totals.employerBurdenInReportingCents : 0;

  const costByCountry = useMemo<EChartsOption>(() => ({
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const value = typeof p.value === 'number' ? p.value : 0;
        return `${p.name}<br/>${fmt(value * 100, ccy)} (${p.percent ?? 0}%)`;
      },
    },
    legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['45%', '70%'], center: ['50%', '44%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 2 }, label: { show: false },
      // value in major units (cents/100) so the formatter reads cleanly
      data: (s?.breakdown ?? []).map((b, i) => ({
        name: `${countryFlag(b.countryCode)} ${b.countryCode}`,
        value: ((b.grossInReportingCents ?? 0) + (b.employerBurdenInReportingCents ?? 0)) / 100,
        itemStyle: { color: PALETTE[i % PALETTE.length] },
      })),
    }],
  }), [s, ccy]);

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-6xl mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('globalPayroll.eyebrow', { defaultValue: 'Cross-border' })}</p>
            <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('globalPayroll.title', { defaultValue: 'Global payroll' })}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('globalPayroll.subtitle', { defaultValue: 'Total employer cost across every jurisdiction, FX-normalized into your reporting currency.' })}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-ink-300 rounded-lg px-2 py-1.5 text-sm focus:border-accent-500 focus:outline-none">
              {[0, 1, 2].map((d) => { const y = now.getFullYear() - d; return <option key={y} value={y}>{y}</option>; })}
            </select>
            <Link to="/app/payroll" className="text-xs font-semibold text-accent-700 hover:text-accent-600">{t('globalPayroll.toRuns', { defaultValue: 'Runs →' })}</Link>
          </div>
        </header>

        {summary.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
        {s && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Tile icon={Building2} label={t('globalPayroll.headcount', { defaultValue: 'Headcount paid' })} value={String(s.totals.headcount)} />
              <Tile icon={Coins} label={t('globalPayroll.gross', { defaultValue: 'Gross' })} value={fmt(s.totals.grossInReportingCents, ccy)} />
              <Tile icon={Wallet} label={t('globalPayroll.net', { defaultValue: 'Net paid' })} value={fmt(s.totals.netInReportingCents, ccy)} />
              <Tile icon={Globe2} label={t('globalPayroll.cash', { defaultValue: 'Employer cash outlay' })} value={fmt(cashOutlay, ccy)} sub={`${ccy}`} />
            </div>

            {!s.totals.complete && (
              <div className="rounded-lg border border-warn-500/30 bg-warn-50 px-3 py-2 text-xs text-warn-700">
                {t('globalPayroll.incomplete', { defaultValue: 'Some currencies are missing an FX rate, so totals are partial.' })}
              </div>
            )}

            {s.breakdown.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-ink-300 bg-white p-10 text-center">
                <p className="text-sm text-ink-600">{t('globalPayroll.empty', { defaultValue: 'No payroll runs in this year yet.' })}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title={t('globalPayroll.costByCountry', { defaultValue: 'Employer cost by country' })}><EChart option={costByCountry} /></Card>
                <Card title={t('globalPayroll.breakdown', { defaultValue: 'Per-jurisdiction breakdown' })}>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="text-ink-500">
                        <tr>
                          <th className="text-start font-semibold py-2">{t('globalPayroll.col_country', { defaultValue: 'Country' })}</th>
                          <th className="text-end font-semibold py-2">{t('globalPayroll.col_hc', { defaultValue: 'HC' })}</th>
                          <th className="text-end font-semibold py-2">{t('globalPayroll.col_fx', { defaultValue: 'FX' })}</th>
                          <th className="text-end font-semibold py-2">{t('globalPayroll.col_gross', { defaultValue: 'Gross' })}</th>
                          <th className="text-end font-semibold py-2">{t('globalPayroll.col_cash', { defaultValue: 'Cash outlay' })}</th>
                        </tr>
                      </thead>
                      <tbody className="num divide-y divide-ink-100">
                        {s.breakdown.map((b) => (
                          <tr key={`${b.countryCode}-${b.currency}`} className="hover:bg-ink-50/40">
                            <td className="py-2 font-medium text-ink-900">{countryFlag(b.countryCode)} {b.countryCode} <span className="text-[10px] text-ink-400">{b.currency}</span></td>
                            <td className="py-2 text-end text-ink-700">{b.headcount}</td>
                            <td className="py-2 text-end text-ink-400">{b.rateToReporting != null ? b.rateToReporting.toFixed(4) : '—'}</td>
                            <td className="py-2 text-end text-ink-900">{fmt(b.grossInReportingCents, ccy)}</td>
                            <td className="py-2 text-end text-ink-900">{fmt((b.grossInReportingCents ?? 0) + (b.employerBurdenInReportingCents ?? 0), ccy)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            <p className="text-[11px] text-ink-400">{t('globalPayroll.fxNote', { date: s.fxAsOf, defaultValue: 'FX rates as of {{date}}, normalized to {{ccy}}.', ccy })}</p>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Tile({ icon: Icon, label, value, sub }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <Icon className="h-5 w-5 text-accent-600" />
      <p className="num mt-2 text-xl font-bold text-ink-900">{value}</p>
      <p className="text-xs text-ink-500 font-medium">{label}{sub && <span className="text-ink-400"> · {sub}</span>}</p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-ink-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}
